import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface DropTrailerFlowDateDialogData {
  title: string;
  label: string;
  eyebrow?: string;
  message?: string;
  initialDate?: string | null;
  initialTime?: string | null;
  confirmText?: string;
}

export interface DropTrailerFlowDateDialogResult {
  date: string;
  time: string;
}

@Component({
  selector: 'app-drop-trailer-flow-date-dialog',
  template: `
    <div class="w-[440px] max-w-[94vw] overflow-hidden rounded-2xl bg-white text-slate-950 shadow-2xl">
      <div class="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white px-6 py-5">
        <p class="m-0 text-xs font-black uppercase tracking-[0.14em] text-sky-600">{{ data.eyebrow || 'Trailer Flow' }}</p>
        <h2 class="m-0 mt-1 text-xl font-black leading-7 text-slate-950">{{ data.title }}</h2>
        <p class="m-0 mt-1 text-sm font-semibold text-slate-500">{{ data.label }}</p>
      </div>

      <div class="space-y-4 px-6 py-5">
        <p *ngIf="data.message" class="m-0 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold leading-6 text-amber-800">
          {{ data.message }}
        </p>

        <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner shadow-slate-200/40">
          <label class="block min-w-0">
            <span class="mb-2 block text-xs font-black uppercase tracking-[0.12em] text-slate-500">Date and Time</span>
            <input
              type="datetime-local"
              step="60"
              class="box-border h-12 w-full min-w-0 max-w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-black text-slate-950 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
              [(ngModel)]="dateTimeValue"
            />
          </label>
        </div>

        <button
          type="button"
          class="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-black text-slate-600 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
          (click)="setNow()"
        >
          Use Now
        </button>
      </div>

      <div class="flex items-center justify-end gap-3 bg-white px-6 pb-6">
        <button
          type="button"
          class="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 px-4 text-sm font-black text-slate-700 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
          (click)="onCancel()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="inline-flex min-h-10 items-center justify-center rounded-xl bg-sky-600 px-4 text-sm font-black text-white shadow-lg shadow-sky-950/20 transition hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
          [disabled]="!dateTimeValue"
          (click)="onConfirm()"
        >
          {{ data.confirmText || 'Mark Complete' }}
        </button>
      </div>
    </div>
  `,
})
export class DropTrailerFlowDateDialogComponent {
  dateTimeValue = '';

  constructor(
    private dialogRef: MatDialogRef<DropTrailerFlowDateDialogComponent, DropTrailerFlowDateDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: DropTrailerFlowDateDialogData
  ) {
    this.dateTimeValue = this.composeDateTimeValue(data.initialDate, data.initialTime) || this.nowDateTime();
  }

  setNow(): void {
    this.dateTimeValue = this.nowDateTime();
  }

  onConfirm(): void {
    const parsedValue = this.parseDateTimeValue(this.dateTimeValue);
    if (!parsedValue) return;

    this.dialogRef.close({
      date: parsedValue.date,
      time: parsedValue.time,
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private nowDateTime(): string {
    const now = new Date();
    return `${this.formatDatePart(now)}T${this.formatTimePart(now)}`;
  }

  private formatDatePart(date: Date): string {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-');
  }

  private formatTimePart(date: Date): string {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  private composeDateTimeValue(dateValue: string | null | undefined, timeValue: string | null | undefined): string {
    const date = this.normalizeDate(dateValue);
    const time = this.normalizeTime(timeValue);
    return date && time ? `${date}T${time}` : '';
  }

  private parseDateTimeValue(value: string): { date: string; time: string } | null {
    const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
    return match ? { date: match[1], time: match[2] } : null;
  }

  private normalizeDate(value: string | null | undefined): string {
    if (!value) return '';
    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }

  private normalizeTime(value: string | null | undefined): string {
    if (!value) return '';
    const match = String(value).match(/(?:^|\s|T)(\d{2}:\d{2})/);
    return match ? match[1] : '';
  }
}
